import React, { useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
// import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import '../styles/captable.scss'
import { DateTime } from 'luxon'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface Captable {
  id: number
  shareholderId: string
  shareholdings: string
  designation: string
  security_class: string[]
  phone: string
  transactions: number
  fully_diluted_shares: number
  amount_invested: number
  ownership: string
  ownership_diff: number
  alltransactions: any[]
  profileImage: {
    id: string
    name: string
  } | null
}

export const DataTableServerSideFinancial = ({
  shareholders,
  subtotals,
}: {
  shareholders: Captable[]
  subtotals: any
}) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const { formatMessage } = useIntl()
  const [expandedYourShareholdings, setExpandedYourShareholdings] = useState<number | null>(null)
  //   const [expandedOthersShareholdings, setExpandedOthersShareholdings] = useState<number | null>(
  //     null
  //   )
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedYourShareholdings(expandedYourShareholdings === rowIndex ? null : rowIndex)
  }

  //   const handleOthersRowToggle = (rowIndex: number) => {
  //     setExpandedOthersShareholdings(expandedOthersShareholdings === rowIndex ? null : rowIndex)
  //   }

  const columns = React.useMemo<ColumnDef<Captable, any>[]>(
    () => [
      {
        accessorKey: 'shareholdings',
        id: 'shareholdings',
        header: 'Shareholdings',
        cell: (info) => (
          <div className='d-flex align-items-center gap-2 ps-4'>
            <DisplayImage
              imgName={info.row?.original?.profileImage?.name || ''}
              alt={'profile-pic'}
              height={40}
              width={40}
              className='rounded'
            />
            <div>
              <h5 className='m-0 font-size-13'>{info.row.original.shareholdings}</h5>
              <p className='m-0'>{info.row.original?.designation}</p>
            </div>
          </div>
        ),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'transactions',
        id: 'transactions',
        header: 'Transactions',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'security_class',
        id: 'security_class',
        header: 'Security Classes',
        cell: (info) => {
          const securityClasses = info.getValue()
          return Array.isArray(securityClasses) && securityClasses.length > 0 ? (
            <div className='d-flex flex-wrap gap-1'>
              {securityClasses.map((securityClass, index) => (
                <span
                  key={index}
                  className={`badge text-white`}
                  style={{ backgroundColor: securityClass.tagColor }}
                >
                  {securityClass.classCode}
                </span>
              ))}
            </div>
          ) : (
            <span>{formatMessage({ id: 'No security classes' })}</span>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'fully_diluted_shares',
        id: 'fully_diluted_shares',
        header: 'Fully Diluted Shares',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'amount_invested',
        id: 'amount_invested',
        header: 'Amount Invested',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'ownership',
        id: 'ownership',
        header: 'Ownership',
        cell: (info) => `${info.getValue()}%`,
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line
  )

  const table = useReactTable({
    data: shareholders as Captable[],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  function formatDate(inputDate: any) {
    const date = DateTime.fromISO(inputDate).setLocale('en')
    const day = date.day
    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
          ? 'nd'
          : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th'

    return `${day}${suffix} ${date.toFormat('MMMM yyyy')}`
  }

  return (
    <>
      <div className='bg-white rounded' style={{ overflowX: 'auto' }}>
        <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
          <thead className='custom-thead border-bottom'>
            {table &&
              table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4 px-4'>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
          </thead>
          <tbody>
            {table &&
              table.getRowModel().rows.map((row, index) => (
                <React.Fragment key={row.id}>
                  <tr
                    key={row.id}
                    onClick={() => handleYourRowToggle(index)}
                    className={`border-bottom ${expandedYourShareholdings === index
                      ? 'expanded-row-captable bg-light'
                      : 'bg-white'
                      }`}
                  >
                    {row.getVisibleCells().map((cell, index) =>
                      index === 5 ? (
                        <td key={cell.id} className='pt-5 border-0'>
                          <div>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            <div className='text-success'>
                              {row.original.ownership_diff
                                ? `+${row.original.ownership_diff}%`
                                : ''}
                            </div>
                          </div>
                        </td>
                      ) : index === 0 ? (
                        <td key={cell.id} className='pt-3 border-0'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ) : (
                        <td key={cell.id} className='pt-5 border-0'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      )
                    )}
                  </tr>
                  {expandedYourShareholdings === index &&
                    row.original.alltransactions &&
                    row.original.alltransactions.map((transaction: any, transactionIndex: any) => (
                      <tr
                        key={transactionIndex}
                        className={`border-bottom ${expandedYourShareholdings === index
                          ? 'expanded-row-captable bg-light'
                          : 'bg-white'
                          }`}
                      >
                        <td className='min-w-120px pb-6 pt-4'></td>
                        <td className='min-w-120px pb-6 pt-4'>
                          {formatDate(transaction.createdAt)}
                        </td>
                        <td className={`min-w-120px pb-6 pt-4 `}>
                          <span
                            className='badge text-white'
                            style={{ backgroundColor: transaction.tagColor }}
                          >
                            {transaction.className}
                          </span>
                        </td>
                        <td className='min-w-120px pb-6 pt-4'>
                          {transaction.numberOfShares || transaction.warrantIssued || '-'}
                        </td>
                        <td className='min-w-120px pb-6 pt-4'>
                          {transaction.totalTransactionValue ||
                            transaction.investedAmount ||
                            transaction.amountPaid ||
                            '-'}
                        </td>
                        <td className='min-w-120px pb-6 pt-4'>-</td>
                        {/* <td className='min-w-120px pb-6 pt-4'></td> */}
                      </tr>
                    ))}
                </React.Fragment>
              ))
            }
          </tbody>
          <tfoot style={{
            background: '#4776E624',
            overflowX: 'auto',
          }}>
            <tr>
              <td className='ps-4'>{formatMessage({ id: 'Subtotal' })}</td>
              <td className='d-flex'>{`${subtotals.totalTransactionCount} ${formatMessage({ id: 'Transactions' })}`}</td>
              <td></td>
              <td>{subtotals.totalFullyDilutedShares}</td>
              <td>{subtotals.totalAmountInvested}</td>
              <td>{formatMessage({ id: '100%' })}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </>
  )
}
